import { useEffect, useRef } from 'react';
import { isProBuilderOpenEdge, useProBuilderOpenStore } from './pro-builder-open-store';

/**
 * FRONT-3003: closes a full-screen surface the instant the Pro Builder window
 * OPENS — the "modal viewers are mutually exclusive, never stacked" rule
 * (CLAUDE.md), from the other side. Pro Builder is mounted once in the app
 * shell and opened from anywhere (the sidebar, the command palette, Settings),
 * so it cannot close what is already on screen itself; every such surface
 * watches this edge instead, the way they already watch a new operator-panel
 * request (`use-close-on-operator-panel-request.ts`). Pro Builder's own host
 * covers the reverse: it hides on their open requests.
 *
 * Called by: the desktop operator panel (`operator-panel-host.tsx`), the chat
 * window (`inbox-chat-modal.tsx`) and the Settings modal shell.
 *
 * `onClose` is read through a ref, so the effect fires only on the edge, never
 * because the caller re-rendered with a new `onClose`. Seeded with the
 * mount-time value (see `isProBuilderOpenEdge`).
 */
export function useCloseOnProBuilderOpen(onClose: () => void): void {
  const open = useProBuilderOpenStore((s) => s.open);
  const previous = useRef(open);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (isProBuilderOpenEdge(previous.current, open)) onCloseRef.current();
    previous.current = open;
  }, [open]);
}
